import { iterable } from 'utils/array'
import numeral from 'numeral'
import { isNil, propOr, reverse } from 'ramda'
import { isNotNilOrEmpty } from 'utils/ramda'
import { isStaffUser } from 'utils/user'

export const FOLDERS = [
  'wiki',
  'guru',
  'reporting',
  'planning',
  'floorplans',
  'frs',
  'other',
  'files',
  'notes',
  'model',
  'accessControl',
  'analytics',
  'units',
  'inbox',
  'taskReports',
  'actions',
  'incidents',
  'compliance',
  'noticeBoard'
]

export const ADDRESS_CLASSIFICATION_PRIMARY_LABELS = {
  C: 'Commercial',
  L: 'Land',
  M: 'Military',
  O: 'Other (Ordnance Survey only)',
  P: 'Parent Shell',
  R: 'Residential',
  U: 'Unclassified',
  X: 'Dual Use',
  Z: 'Object of Interest'
}

export const ADDRESS_CLASSIFICATION_SECONDARY_LABELS = {
  C: 'Commercial',
  CA: 'Agricultural',
  CB: 'Ancillary Building',
  CC: 'Community Services',
  CE: 'Education',
  CH: 'Hotel / Motel / Boarding / Guest House',
  CI: 'Industrial',
  CL: 'Leisure',
  CM: 'Medical',
  CN: 'Animal Centre',
  CO: 'Office',
  CR: 'Retail',
  CS: 'Storage Land',
  CT: 'Transport',
  CU: 'Utility',
  CX: 'Emergency / Rescue Service',
  LD: 'Development',
  LP: 'Park',
  M: 'Military',
  MA: 'Army',
  MB: 'Ancillary Building',
  MF: 'Air Force',
  MG: 'Defence Estates',
  MN: 'Navy',
  OR: 'Royal Mail Infrastructure',
  P: 'Parent Shell',
  PP: 'Property Shell',
  R: 'Residential',
  RD: 'Dwelling',
  RG: 'Garage',
  RH: 'House In Multiple Occupation',
  RI: 'Residential Institution',
  U: 'Unclassified',
  UC: 'Awaiting Classification',
  UP: 'Pending Internal Investigation',
  X: 'Dual Use',
  ZM: 'Monument',
  ZS: 'Stately Home',
  ZW: 'Place Of Worship'
}

export const getAddressClassificationDetails = classificationCode => {
  const normalizedCode = String(classificationCode || '').trim().toUpperCase()

  if (!normalizedCode) {
    return {
      primary: null,
      secondary: null
    }
  }

  const primaryCode = normalizedCode.slice(0, 1)
  const secondaryCode = normalizedCode.length >= 2 ? normalizedCode.slice(0, 2) : primaryCode

  return {
    primary: ADDRESS_CLASSIFICATION_PRIMARY_LABELS[primaryCode]
      ? {
        code: primaryCode,
        label: ADDRESS_CLASSIFICATION_PRIMARY_LABELS[primaryCode]
      }
      : null,
    secondary: ADDRESS_CLASSIFICATION_SECONDARY_LABELS[secondaryCode]
      ? {
        code: secondaryCode,
        label: ADDRESS_CLASSIFICATION_SECONDARY_LABELS[secondaryCode]
      }
      : null
  }
}

export const getNoteFloorsOptions = bp => () => {
  const groundFloor = bp.groundFloor
    ? [{ value: 'ground', label: 'Ground floor' }]
    : []
  const lowerGroundFloor = bp.lowerGroundFloor
    ? [{ value: 'lower_ground', label: 'Lower ground floor' }]
    : []
  const underground = iterable(bp.undergroundFloorsNumber).map(
    (element, index) => ({
      value: -(index + 1),
      label: `Basement level ${index + 1}`
    })
  )
  const floors = iterable(bp.floorsNumber).map((element, index) => ({
    value: index + 1,
    label: `${numeral(index + 1).format('Oo')} floor`
  }))

  return [
    { value: 'general', label: 'General note' },
    { value: 'notice', label: 'Notice for all units' },
    ...reverse(floors),
    ...groundFloor,
    ...lowerGroundFloor,
    ...underground
  ]
}

export const getFloorOptions = (bp, short) => {
  let floors = []

  if (bp.undergroundFloorsNumber > 0) {
    for (let i = 1; i <= bp.undergroundFloorsNumber; i++) {
      floors.push({
        label: short ? `${-i}` : `Basement level ${i}`,
        value: `${-i}`
      })
    }
  }

  floors.reverse()

  if (bp.lowerGroundFloor) {
    floors.push({
      label: short ? 'LG' : 'Lower Ground Floor',
      value: `lower_ground`
    })
  }

  if (bp.groundFloor) {
    floors.push({ label: short ? 'G' : 'Ground Floor', value: parseInt('0') })
  }

  if (bp.floorsNumber > 0) {
    for (let i = 1; i <= bp.floorsNumber; i++) {
      floors.push({
        label: short ? i : `${numeral(i).format('oO')} floor`,
        value: `${i}`
      })
    }
  }
  return floors
}

export const getFloorName = floor => {
  switch (true) {
    case floor === 'lower_ground':
      return 'Lower ground floor'
    case floor === '0':
    case floor === 'ground':
      return 'Ground floor'
    case floor < 0:
      return `Basement level ${-floor}`
    case floor > 0:
      return `${numeral(floor).format('Oo')} floor`
    default:
      return ''
  }
}

export const getShortFloorName = floor => {
  if (floor === null || floor === undefined) return ''
  const raw = `${floor}`.trim()
  if (raw === '') return ''
  if (raw === 'lower_ground' || raw === 'LG') return 'Lower ground'
  if (raw === 'ground') return 'Ground'
  const n = Number(raw)
  if (!Number.isNaN(n)) {
    if (n === 0) return 'Ground'
    if (n < 0) return `Basement ${-n}`
    return raw
  }
  const pretty = raw.replace(/_/g, ' ')
  return pretty.charAt(0).toUpperCase() + pretty.slice(1)
}

export const getFileTypeName = (typeId, allTypes) => {
  const found = allTypes.find(e => e.id === typeId)
  const name = propOr('Uncategorised', 'name', found)
  const normalised = String(name).trim().toLowerCase()
  return normalised === '' || normalised === 'null' || normalised === 'undefined'
    ? 'Uncategorised'
    : name
}

export const getBuildingTypeValueType = type => {
  switch (type) {
    case 'assembly':
    case 'recreation':
    case 'leisure':
    case 'infrastructure':
    case 'labs':
      return 'Area (sqm)'
    case 'car_parks':
    case 'storage':
    case 'industrial':
    case 'office':
    case 'retail':
      return 'Lettable area (sqm)'
    case 'residential.dwelling':
      return 'Number of units/apartments/flats etc'
    case 'residential.institutional':
    case 'residential.other':
    case 'residential.dwelling.sole_unit':
    case 'residential':
    case 'hotel':
      return 'Units'
    default:
      return 'Area / Units / Flats'
  }
}

export const getAssignedUsers = bp => {
  const all = isNotNilOrEmpty(bp)
    ? [
        ...propOr([], 'owners', bp).map(user => ({
          ...user,
          relationType: 'owner',
          pivot: {
            ...(Array.isArray(user.pivot) ? user.pivot[0] : user.pivot ?? {}),
            id: user.pivot?.id ?? user.pivot?.[0]?.id ?? null,
            buildingPassportId: user.pivot?.buildingPassportId ?? user.pivot?.[0]?.buildingPassportId ?? bp?.id,
          }
        })),
        ...propOr([], 'agents', bp).map(user => ({
          ...user,
          relationType: 'agent',
          pivot: {
            ...(Array.isArray(user.pivot) ? user.pivot[0] : user.pivot ?? {}),
            id: user.pivot?.id ?? user.pivot?.[0]?.id ?? null,
            buildingPassportId: user.pivot?.buildingPassportId ?? user.pivot?.[0]?.buildingPassportId ?? bp?.id,
          }
        })),
        ...propOr([], 'occupants', bp).map(user => ({
          ...user,
          relationType: 'occupant',
          pivot: {
            ...(Array.isArray(user.pivot) ? user.pivot[0] : user.pivot ?? {}),
            id: user.pivot?.id ?? user.pivot?.[0]?.id ?? null,
            buildingPassportId: user.pivot?.buildingPassportId ?? user.pivot?.[0]?.buildingPassportId ?? bp?.id,
          }
        })),
        ...propOr([], 'principalAccountablePersons', bp).map(user => ({
          ...user,
          relationType: 'principalAccountablePerson',
          pivot: {
            ...(Array.isArray(user.pivot) ? user.pivot[0] : user.pivot ?? {}),
            id: user.pivot?.id ?? user.pivot?.[0]?.id ?? null,
            buildingPassportId: user.pivot?.buildingPassportId ?? user.pivot?.[0]?.buildingPassportId ?? bp?.id,
          }
        })),
        ...propOr([], 'principalDesigners', bp).map(user => ({
          ...user,
          relationType: 'principalDesigner',
          pivot: {
            ...(Array.isArray(user.pivot) ? user.pivot[0] : user.pivot ?? {}),
            id: user.pivot?.id ?? user.pivot?.[0]?.id ?? null,
            buildingPassportId: user.pivot?.buildingPassportId ?? user.pivot?.[0]?.buildingPassportId ?? bp?.id,
          }
        })),
        ...propOr([], 'responsiblePersons', bp).map(user => ({
          ...user,
          relationType: 'responsiblePerson',
          pivot: {
            ...(Array.isArray(user.pivot) ? user.pivot[0] : user.pivot ?? {}),
            id: user.pivot?.id ?? user.pivot?.[0]?.id ?? null,
            buildingPassportId: user.pivot?.buildingPassportId ?? user.pivot?.[0]?.buildingPassportId ?? bp?.id,
          }
        })),
        ...propOr([], 'prospectivePurchasers', bp)
          .filter(pp => !isNil(pp.firstName))
          .map(user => ({ ...user, relationType: 'prospectivePurchaser' }))
      ]
    : []
  // Defence: hide Building Passport staff users from customer-facing views,
  // mirroring the server filter on every access-control endpoint.
  return all.filter(u => !isStaffUser(u))
}

// Floor labels in building order, lowest first: B2, B1, LG, G, 1, 2 …
// Consecutive floors collapse into a range ("B1 to 1"), so a unit spanning
// basement, lower ground, ground and first floor reads as one span rather
// than "G, LG, -1, 1". Accepts numbers or the string "lower_ground".
const floorLabel = floor => {
  if (floor === -0.5) return 'LG'
  if (floor === 0) return 'G'
  if (floor < 0) return `B${-floor}`
  return `${floor}`
}

export const getFloorsIntervals = floors => {
  const unique = Array.from(
    new Set(
      (floors || [])
        .map(num => (num === 'lower_ground' || num === 'LG' ? -0.5 : Number(num)))
        .filter(num => !Number.isNaN(num))
    )
  ).sort((a, b) => a - b)

  if (!unique.length) return []

  // Neighbouring floors are one level apart; Lower Ground sits half a step
  // between the first basement and Ground, so a step of 0.5 is also contiguous.
  const runs = []
  unique.forEach((floor, index) => {
    const previous = unique[index - 1]
    if (index > 0 && floor - previous <= 1) {
      runs[runs.length - 1].push(floor)
    } else {
      runs.push([floor])
    }
  })

  return runs.map(run =>
    run.length >= 3
      ? `${floorLabel(run[0])} to ${floorLabel(run[run.length - 1])}`
      : run.map(floorLabel).join(', ')
  )
}
